import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { toast } from "react-toastify";
import { updateRcaAssignedTo } from "../../../actions/qc";
import { getUser } from "../../../utils/helpers";
import { resolveRcaAssigneeDisplay } from "./utils/rcaUtils";
import "../QC/QC.css";

/**
 * Inline assignee dropdown — same interaction pattern as QC Dashboard Assign To.
 */
export default function RcaAssignToSelect({
  record,
  assigneeOptions = [],
  assigneeOptionsLoading = false,
  readOnly = false,
  onUpdated,
}) {
  const recordAssignedTo = record?.assigned_to;
  const recordAssignedToName = record?.assigned_to_name;
  const recordRcaId = record?.rca_id;
  const recordId = record?.id;

  const [assignTo, setAssignTo] = useState(recordAssignedTo ?? undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignTo(recordAssignedTo ?? undefined);
  }, [recordAssignedTo, recordRcaId, recordId]);

  const optionsForSelect = useMemo(() => {
    const baseOptions = assigneeOptions.filter((option) => option.value !== "");
    const assigneeFromApi = recordAssignedTo != null && recordAssignedTo !== ""
      && recordAssignedToName
      ? { value: recordAssignedTo, label: recordAssignedToName }
      : null;
    if (!assigneeFromApi) {
      return baseOptions;
    }
    return [
      assigneeFromApi,
      ...baseOptions.filter(
        (option) => String(option.value) !== String(recordAssignedTo),
      ),
    ];
  }, [assigneeOptions, recordAssignedTo, recordAssignedToName]);

  if (readOnly) {
    return <span>{resolveRcaAssigneeDisplay(record)}</span>;
  }

  const user = getUser();
  const canEdit = Boolean(user?.permissionMap?.edit_qc_assign_to);
  if (!canEdit) {
    return <span>{resolveRcaAssigneeDisplay(record)}</span>;
  }

  const handleSelect = async (value) => {
    const previousValue = recordAssignedTo ?? undefined;
    setAssignTo(value);
    setSaving(true);
    const rcaId = record?.rca_id ?? record?.id;
    const response = await updateRcaAssignedTo({
      rca_pk: rcaId,
      assigned_to: value,
      user_id: user.id,
    });
    setSaving(false);

    if (!response.success) {
      setAssignTo(previousValue);
      toast.error(response.message || "Failed to update assign to");
      return;
    }

    const selectedLabel = optionsForSelect.find(
      (option) => String(option.value) === String(value),
    )?.label ?? "";

    if (onUpdated) {
      onUpdated({
        ...record,
        assigned_to: value,
        assigned_to_name: selectedLabel,
      });
    }
  };

  return (
    <Select
      showSearch
      className="QCTable__AssignTo"
      popupClassName="QCTable__AssignTo__Menu"
      loading={assigneeOptionsLoading || saving}
      options={optionsForSelect}
      value={assignTo}
      onSelect={handleSelect}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      filterOption={(inputValue, option) => (
        String(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
      )}
      listHeight={400}
    />
  );
}
